import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { DealFolderForm, IFolder, IRole, UrlQueryParams } from "store/types";

import { useFoldersSelect, useUserRoleSelector } from "../../store";
import { JSON_to_URLEncoded } from "../../utils";
import { isRoleAnalyst } from "../../utils/user";
import { useUrlHooks } from "./use-url-hooks";

export type UrlParamsToRemove = keyof UrlQueryParams;

export interface IUseNavigationHooks {
  setParams: (data: ISetParamsData) => void;
  removeParams: (urlParams: UrlParamsToRemove[]) => void;
  getFormPathName: (dealId: number, formId: number) => string;
  getFilePathName: (dealId: number, fileId: number) => string;
  getFolderPathName: (dealId: number, folderId: number) => string;
  getEmailsPathName: (dealId: number, folderId: number) => string;
  getNewsPathName: (dealId: number, folderId: number) => string;
  getEmailPathName: (dealId: number, emailId: number) => string;
  goToFirstForm: (dealId: number, form?: DealFolderForm) => void;
  goToFirstFolderMetadata: () => void;
  getNotePathName: (dealId: number, noteId: number) => string;
  getNoteMetaPathName: (dealId: number, noteId: number) => string;
  goToDeal: (dealId?: number) => void;
}

interface ISetParamsData {
  search?: UrlQueryParams;
  pathname?: string;
  state?: { [key: string]: any };
  usePush?: boolean;
}

export function useNavigationHooks(): IUseNavigationHooks {
  const { replace, push } = useHistory();
  const { queryParams } = useUrlHooks();
  const searchObj: UrlQueryParams = {};
  const folder: IFolder | undefined = useFoldersSelect({ kind: "folder", parent_id: null })[0];

  const role: IRole | null = useUserRoleSelector();

  const setParams = useCallback(
    (data: ISetParamsData): void => {
      const { pathname, state } = data;
      // if (Object.keys(urlParams).length) {
      const querySearch = {
        ...queryParams,
        ...data.search
      };
      if (data.usePush) {
        push({
          search: JSON_to_URLEncoded(querySearch),
          pathname,
          state
        });
      } else {
        replace({
          search: JSON_to_URLEncoded(querySearch),
          pathname,
          state
        });
      }
      // }
    },
    [replace, queryParams, push]
  );

  const removeParams = useCallback(
    (urlParams: UrlParamsToRemove[]) => {
      if (urlParams.length) {
        const querySearch = {
          ...queryParams
        };
        urlParams.forEach((urlParam: UrlParamsToRemove): void => {
          delete querySearch[urlParam];
        });
        replace({
          search: JSON_to_URLEncoded(querySearch)
        });
      }
    },
    [replace, queryParams]
  );

  const getFormPathName = useCallback((dealId: number, formId: number) => {
    return `/main/deal/${dealId}/workspace/form/${formId}`;
  }, []);

  const getFilePathName = useCallback((dealId: number, fileId: number) => {
    return `/main/deal/${dealId}/workspace/file/${fileId}`;
  }, []);

  const getFolderPathName = useCallback((dealId: number, folderId: number) => {
    return `/main/deal/${dealId}/workspace/folder/${folderId}`;
  }, []);

  const getEmailsPathName = useCallback((dealId: number, folderId: number) => {
    return `/main/deal/${dealId}/workspace/emails/${folderId}`;
  }, []);

  const getEmailPathName = useCallback((dealId: number, emailId: number) => {
    return `/main/deal/${dealId}/workspace/email/${emailId}`;
  }, []);

  const getNewsPathName = useCallback((dealId: number, folderId: number) => {
    return `/main/deal/${dealId}/workspace/news/${folderId}`;
  }, []);

  const getNotePathName = useCallback((dealId: number, noteId: number) => {
    return `/main/deal/${dealId}/workspace/note/${noteId}`;
  }, []);

  const getNoteMetaPathName = useCallback((dealId: number, noteId: number) => {
    return `/main/deal/${dealId}/workspace/note_info/${noteId}`;
  }, []);

  const goToFirstForm = useCallback(
    (dealId: number, form?: DealFolderForm): void => {
      if (form) {
        setParams({
          search: { section: form.section_id },
          pathname: getFormPathName(form.deal_id, form.id)
        });
      } else {
        // if no forms to render

        if (isRoleAnalyst({ role })) {
          // if analyst and no folders
          replace(`/main/deal/${dealId}/template`); // go to template
        } else {
          // all other possible cases
          setParams({
            pathname: `/main/deal/${dealId}/form-not-exist` // go to form not exist
          });
        }
      }
    },
    [getFormPathName, setParams]
  );

  const goToFirstFolderMetadata = useCallback((): void => {
    if (folder) {
      searchObj.section = folder.id;
      push({
        pathname: getFolderPathName(folder.deal_id!, folder.id),
        search: JSON_to_URLEncoded(searchObj)
      });
    }
  }, [getFolderPathName, JSON_to_URLEncoded, searchObj, folder]);

  const goToDeal = useCallback((id?: number) => {
    if (!id) return;
    push({ pathname: `/main/deal/${id}/dashboard` });
  }, []);

  return {
    setParams,
    removeParams,
    getFormPathName,
    getFilePathName,
    getFolderPathName,
    getEmailsPathName,
    getEmailPathName,
    goToFirstForm,
    goToFirstFolderMetadata,
    getNewsPathName,
    getNotePathName,
    getNoteMetaPathName,
    goToDeal
  };
}
